import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, CheckCircle, ArrowRight, ShieldCheck, Sparkle, CurrencyInr } from "@phosphor-icons/react";
import { toast } from "sonner";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Billing() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/plans/current").then(r => setPlan(r.data)).catch(() => {});
  }, []);

  const startCheckout = async () => {
    setBusy(true);
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        toast.error("Couldn't load Razorpay. Check your network and retry.");
        setBusy(false);
        return;
      }
      const { data: order } = await api.post("/payments/razorpay/create-order");
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount * 100,
        currency: (order.currency || "INR").toUpperCase(),
        name: "Vendorly",
        description: order.is_intro ? "First month · Intro plan" : "Monthly subscription",
        order_id: order.order_id,
        prefill: order.prefill,
        theme: { color: "#D97757" },
        modal: {
          ondismiss: () => setBusy(false),
        },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful — you're all set 🎉");
            await refresh();
          } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Payment verification failed. Contact support with your payment ID.");
          } finally { setBusy(false); }
        },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(`Payment failed: ${resp?.error?.description || "Please try another method."}`);
        setBusy(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout");
      setBusy(false);
    }
  };

  const active = user?.subscription_status === "active";
  const exp = user?.subscription_expires_at ? new Date(user.subscription_expires_at) : null;
  const price = plan?.amount ?? 199;
  const isIntro = plan?.is_intro;
  const nextPrice = plan?.next_price_after_intro;

  return (
    <div data-testid="billing-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary mb-2">Subscription</div>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">Billing</h1>
        <p className="text-brand-text/70 mt-1">
          {isIntro
            ? `First month ₹${price} · then ₹${nextPrice}/month. No auto-renewal.`
            : `₹${price} per month. No auto-renewal, no hidden fees.`}
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-brand-border rounded-3xl p-8 relative overflow-hidden" data-testid="plan-card">
          {isIntro && !user?.is_admin && (
            <div className="absolute top-0 right-0 flex items-center gap-1 bg-brand-primary text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-bl-2xl">
              <Sparkle size={11} weight="fill" /> Intro offer
            </div>
          )}
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-[0.2em] text-brand-secondary">Current plan</div>
            {user?.is_admin ? (
              <span className="text-xs font-semibold bg-brand-secondary text-white px-3 py-1 rounded-full">Admin — free</span>
            ) : active ? (
              <span className="text-xs font-semibold bg-brand-success text-white px-3 py-1 rounded-full">Active</span>
            ) : (
              <span className="text-xs font-semibold bg-brand-warning text-white px-3 py-1 rounded-full">Not active</span>
            )}
          </div>
          <div className="flex items-baseline gap-2" data-testid="plan-price">
            {user?.is_admin ? (
              <>
                <span className="font-heading text-5xl font-bold tracking-tight">Free</span>
                <span className="text-brand-textMuted">/ owner access</span>
              </>
            ) : (
              <>
                <span className="font-heading text-5xl font-bold tracking-tight">₹{price}</span>
                <span className="text-brand-textMuted">/ {isIntro ? "first month" : "month"}</span>
              </>
            )}
          </div>
          {isIntro && !user?.is_admin && (
            <div className="mt-1 text-sm text-brand-textMuted" data-testid="renewal-note">
              After the first month you'll pay <span className="font-semibold text-brand-text">₹{nextPrice}/month</span> only when you renew.
            </div>
          )}
          <ul className="mt-6 space-y-3 text-sm">
            <Perk>Unlimited customer signups via your QR</Perk>
            <Perk>Unlimited templates</Perk>
            <Perk>WhatsApp campaign sending (real Twilio when configured)</Perk>
            <Perk>Analytics: delivery + read rates</Perk>
            <Perk>Priority support</Perk>
          </ul>
          {exp && (
            <div className="mt-6 text-sm text-brand-textMuted">
              {active ? "Renews / expires on " : "Expired on "}<span className="font-semibold text-brand-text">{exp.toLocaleDateString()}</span>
            </div>
          )}
          {!user?.is_admin && (
            <button onClick={startCheckout} disabled={busy}
              className="mt-8 w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white font-semibold transition-colors disabled:opacity-60"
              data-testid="checkout-btn">
              <CurrencyInr size={18} weight="duotone" />
              {busy ? "Opening Razorpay…" : (active ? `Extend by 1 month (₹${price})` : `Pay ₹${price} & activate`)}
              {!busy && <ArrowRight size={18} weight="bold" />}
            </button>
          )}
        </div>

        <div className="bg-white border border-brand-border rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck size={22} weight="duotone" className="text-brand-secondary" />
            <h3 className="font-heading text-xl font-bold">Safe & simple</h3>
          </div>
          <p className="text-brand-text/75">Payments processed by <span className="font-semibold">Razorpay</span>. Pay with UPI, credit/debit cards, netbanking, wallets, or EMI — all in INR. We never see your card number.</p>
          <div className="mt-6 rounded-2xl border border-brand-border bg-brand-bg p-4 text-sm">
            {plan?.payment_mode === "live" ? (
              <>
                <div className="font-heading font-semibold mb-2">Payment methods accepted</div>
                <ul className="text-brand-textMuted space-y-1">
                  <li><strong>UPI</strong> — Google Pay, PhonePe, Paytm, BHIM, any UPI app (0% fee, instant)</li>
                  <li><strong>Cards</strong> — Visa, Mastercard, RuPay (credit + debit)</li>
                  <li><strong>Netbanking</strong> — all major Indian banks</li>
                  <li><strong>Wallets</strong> — Paytm, Amazon Pay, Airtel Money, and more</li>
                  <li className="pt-1 text-xs">Secure PCI-DSS compliant checkout · settled to your bank in T+3 days</li>
                </ul>
              </>
            ) : (
              <>
                <div className="font-heading font-semibold mb-2">Test mode (India)</div>
                <ul className="text-brand-textMuted space-y-1">
                  <li><strong>UPI (easiest):</strong> use <span className="font-mono text-brand-text">success@razorpay</span> to complete, <span className="font-mono text-brand-text">failure@razorpay</span> to fail</li>
                  <li><strong>Mastercard:</strong> <span className="font-mono text-brand-text">5267 3181 8797 5449</span> · any future expiry · any CVV · OTP <span className="font-mono">1234</span></li>
                  <li><strong>Visa (domestic):</strong> <span className="font-mono text-brand-text">4012 8888 8888 1881</span> · same as above</li>
                  <li><strong>Netbanking:</strong> pick any bank → click Success on the mock screen</li>
                  <li className="text-brand-textMuted text-xs pt-1">⚠️ International cards like <span className="font-mono">4111…</span> are blocked by default on Indian Razorpay accounts.</li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Perk({ children }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle size={18} weight="duotone" className="text-brand-success mt-0.5 shrink-0" />
      <span>{children}</span>
    </li>
  );
}
